import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DepositService } from '@lx/quote-tool/shared/services';
import { Amount } from '@lx/shared/data-access/types';
import { ButtonComponent } from '@lx/shared/ui/button';
import { EqualPipe, PricePipe } from '@lx/shared/ui/pipes';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { AccordionModule } from 'primeng/accordion';
import { BehaviorSubject, Observable } from 'rxjs';
import { VisibilityDirective } from '../../directives';
import { PackageDetailsOrchestratorService } from '../../services';
import { QuoteState, QuoteStore, QuoteToolLiteStore } from '../../store';
import { PACKAGE_DETAILS_CONTAINER_ID } from '../../types';
import { QuoteBreakdownComponent } from '../quote-breakdown/quote-breakdown.component';
import { CardContainerComponent, SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { fadeQuoteBreakDownAnimation } from './package-details.animation';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  selector: 'lx-package-details',
  templateUrl: './package-details.component.html',
  styleUrls: ['./package-details.component.scss'],
  animations: [fadeQuoteBreakDownAnimation],
  imports: [
    CardContainerComponent,
    NgIf,
    PricePipe,
    AsyncPipe,
    EqualPipe,
    QuoteBreakdownComponent,
    AccordionModule,
    ButtonComponent,
    SidePanelComponent,
    VisibilityDirective,
  ],
})
export class PackageDetailsComponent implements OnInit {
  currency$: Observable<string> | undefined;
  packageDetails$: Observable<Partial<QuoteState>> | undefined;

  depositAmounts: Amount[] | undefined;
  depositMessage = '';

  decimalPlaces = 0;

  handheld$: Observable<boolean> | undefined;
  quoteBreakdownVisible$ = new BehaviorSubject<boolean>(false);

  activeFlow$: Observable<Flow> | undefined;

  protected readonly PACKAGE_DETAILS_CONTAINER_ID = PACKAGE_DETAILS_CONTAINER_ID;

  private readonly quoteStore = inject(QuoteStore);
  private readonly qtLiteStore = inject(QuoteToolLiteStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly packageDetailsOrchestratorService = inject(PackageDetailsOrchestratorService);
  private readonly depositService = inject(DepositService);

  ngOnInit() {
    this.currency$ = this.qtLiteStore.selectedCurrency$;
    this.packageDetails$ = this.quoteStore.packageDetails$;
    this.handheld$ = this.breakpointObserverService.handheld$;
    this.quoteBreakdownVisible$ = this.packageDetailsOrchestratorService.quoteBreakdownVisible$;
    this.activeFlow$ = this.qtLiteStore.activeFlow$;
    this.prepareDepositInformation();
  }

  toggleBreakdown() {
    this.packageDetailsOrchestratorService.toggleQuoteBreakdownVisibility();
  }

  hideBreakdown() {
    this.packageDetailsOrchestratorService.setQuoteBreakdownVisibility(false);
  }

  private prepareDepositInformation(): void {
    this.quoteStore.payableData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payableData) => {
      const { required, requiredThreshold, amounts } = payableData || {};
      this.depositAmounts = amounts;
      // Set deposit required message if required
      if (required) {
        this.depositMessage = this.depositService.getDepositRequiredMessage(requiredThreshold);
      } else {
        this.depositMessage = '';
      }
    });
  }

  protected readonly Flows = Flows;
}
